import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import LanguageSwitcher from '../nav/LanguageSwitcher';
import BrandLogo from '../brand/BrandLogo';
import { isClerkEnabled } from '../../config/authUrls.js';
import { OpenAppCtaLink, SignInCtaButton } from './AuthCtaButtons';
import { useHeaderCompact } from './useHeaderCompact';
import { useMarketingHeaderHeightVar } from './useMarketingHeaderHeightVar';
import { resolveRootOnlyCompactClassNames } from './marketingHeaderClassNames';
import styles from './StickyNav.module.scss';

const navLinks = [
  { href: '#product', labelKey: 'nav.product' },
  { href: '#templates', labelKey: 'nav.templates' },
  { href: '#pricing', labelKey: 'nav.pricing' },
  { href: '#faq', labelKey: 'nav.faq' },
];

export const MARKETING_HEADER_TEST_IDS = Object.freeze({
  root: 'marketing-header-root',
  inner: 'marketing-header-inner',
});

export default function StickyNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const isCompact = useHeaderCompact();
  const headerRef = useRef(null);
  useMarketingHeaderHeightVar(headerRef);
  const classNames = resolveRootOnlyCompactClassNames({
    isCompact,
    rootClassName: styles.wrapper,
    compactRootClassName: styles.wrapperCompact,
    innerClassName: styles.shell,
  });
  const isRootRoute = (location.pathname || '/') === '/';

  return (
    <header
      ref={headerRef}
      className={classNames.rootClassName}
      data-header-compact={isCompact ? 'true' : 'false'}
      data-testid={MARKETING_HEADER_TEST_IDS.root}
    >
      <div className="container">
        <nav
          className={classNames.innerClassName}
          aria-label={t('nav.ariaPrimary')}
          data-testid={MARKETING_HEADER_TEST_IDS.inner}
        >
          <BrandLogo className={styles.logo} variant="site" href="/" size="nav" />

          <ul className={styles.navLinks}>
            {navLinks.map((link) => (
              <li key={link.href}>
                <a className={styles.link} href={isRootRoute ? link.href : `/${link.href}`}>
                  {t(link.labelKey)}
                </a>
              </li>
            ))}
          </ul>

          <div className={styles.actions}>
            <LanguageSwitcher className={styles.lang} />
            {isClerkEnabled ? (
              <>
                <SignedOut>
                  <SignInCtaButton
                    className={styles.logInBtn}
                    dataCta="header-log-in"
                  >
                    {t('nav.logIn')}
                  </SignInCtaButton>
                </SignedOut>
                <SignedIn>
                  <OpenAppCtaLink className={styles.logInBtn} dataCta="header-open-app">
                    {t('nav.openApp')}
                  </OpenAppCtaLink>
                </SignedIn>
              </>
            ) : (
              <SignInCtaButton
                className={styles.logInBtn}
                dataCta="header-log-in"
              >
                {t('nav.logIn')}
              </SignInCtaButton>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
